import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ASSETS, CONTACT } from "@/lib/pnp";
import { useAuth } from "@/lib/auth";
import { Instagram, ArrowUpRight, User } from "lucide-react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-[#DFEBE4]"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <div className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-[#DFEBE4] bg-white flex items-center justify-center">
            <img src={ASSETS.logo} alt="P&P Gartenservice Logo" className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-[15px] tracking-tight text-[#082E19]">P&amp;P</span>
            <span className="font-mono-pp text-[10px] uppercase tracking-widest text-[#4F6858]">Gartenservice</span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {[
            { id: "leistungen", label: "Leistungen" },
            { id: "ueber-uns", label: "Über uns" },
            { id: "galerie", label: "Galerie" },
            { id: "kontakt", label: "Kontakt" },
          ].map((it) => (
            <button
              key={it.id}
              data-testid={`nav-${it.id}-btn`}
              onClick={() => scrollTo(it.id)}
              className="font-mono-pp text-[11px] uppercase tracking-[0.18em] text-[#082E19] hover:text-[#17C964] transition-colors"
            >
              {it.label}
            </button>
          ))}
          <Link
            to="/kundenbereich"
            data-testid="nav-kundenbereich-link"
            className="font-mono-pp text-[11px] uppercase tracking-[0.18em] text-[#082E19] hover:text-[#17C964] transition-colors inline-flex items-center gap-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#17C964] inline-block" />
            Kundenbereich
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="header-instagram-link"
            href={CONTACT.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#DFEBE4] text-[#082E19] hover:bg-[#082E19] hover:text-white transition-colors"
          >
            <Instagram size={17} strokeWidth={1.6} />
          </a>
          <Link
            to="/kundenbereich"
            data-testid="header-kundenbereich-btn"
            aria-label="Kundenbereich"
            className="inline-flex md:hidden h-10 w-10 items-center justify-center rounded-full border border-[#DFEBE4] text-[#082E19] hover:bg-[#082E19] hover:text-white transition-colors"
          >
            {user && user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <User size={16} />
            )}
          </Link>
          <Link
            to="/kundenbereich"
            data-testid="header-kundenbereich-btn-desktop"
            className="hidden md:inline-flex pp-btn pp-btn-ghost"
          >
            {user ? (
              <>
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-5 w-5 rounded-full -ml-1" />
                ) : (
                  <User size={15} />
                )}
                Kundenbereich
              </>
            ) : (
              <>
                <User size={15} /> Anmelden
              </>
            )}
          </Link>
          <button
            data-testid="nav-kontakt-btn"
            onClick={() => scrollTo("kontakt")}
            className="pp-btn pp-btn-primary"
          >
            Kontakt
            <ArrowUpRight size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </motion.header>
  );
}
